import React, { useEffect, useState, useCallback } from "react";
import { ListChecks, Inbox } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import RefreshBar from "@/components/RefreshBar";
import { InlineLoader } from "@/components/Spinner";
import { dateLabel } from "@/lib/status";

export default function WorkerToday() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((spin = false) => {
    if (spin) setLoading(true);
    api
      .get("/me/actions/today")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(true); }, [load]);

  if (loading && !data) return <InlineLoader label="Loading your actions…" />;
  const actions = data?.actions || [];

  return (
    <div className="space-y-4 bb-fade-up">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">My actions today</h1>
          <p className="text-sm text-stone-500">{dateLabel(data?.date)} · {actions.length} action{actions.length === 1 ? "" : "s"}</p>
        </div>
        <RefreshBar lastUpdated={data?.last_updated} onRefresh={() => load(true)} loading={loading} testid="worker-today-refresh" />
      </div>

      {actions.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-stone-300 bg-white py-16 text-center" data-testid="worker-today-empty">
          <Inbox className="h-10 w-10 text-stone-300" strokeWidth={1.8} />
          <p className="font-semibold text-stone-600">No actions yet today</p>
          <p className="max-w-xs text-sm text-stone-400">Mark a baldachin as sold from the Beach Map and it will appear here.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-stone-200 bg-white">
          <div className="bb-scroll overflow-x-auto">
          <table className="w-full min-w-[480px] text-sm" data-testid="worker-today-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                <th className="px-4 py-3 font-semibold">Time</th>
                <th className="px-4 py-3 font-semibold">Baldachin</th>
                <th className="px-4 py-3 font-semibold">Action</th>
                <th className="px-4 py-3 text-right font-semibold">Count after</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a) => (
                <tr key={a.id} className="border-b border-stone-100 last:border-0" data-testid={`worker-action-${a.id}`}>
                  <td className="px-4 py-3 font-medium text-stone-500">{a.time}</td>
                  <td className="px-4 py-3 font-heading text-base font-bold">#{a.baldachin_number}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${a.action_type === "sold" ? "bg-sky-50 text-sky-700" : "bg-orange-50 text-orange-700"}`}>
                      {a.action_label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-bold">{a.count_after}x</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-stone-400">
        <ListChecks className="h-3.5 w-3.5" /> This list is read-only. You cannot edit or delete your actions.
      </p>
    </div>
  );
}
